import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';

import {
  captureSidekickActingOrganizationScope,
  type SidekickActingOrganizationScope,
} from '../api/actingOrganization';
import {
  createConversationBranch,
  selectConversationBranchNavigation,
  selectConversationId,
  selectConversationScopeVersion,
  selectIsStreaming,
  selectMessages,
  switchConversationBranch,
} from '../state';
import type { StateWithSidekickChat } from '../state/chatTypes';
import type { ConversationBranchNavigation } from '../state/conversation-branch-selectors';
import { getLatestUserMessage } from '../utils/get-latest-user-message';
import {
  isConversationBranchParentCurrent,
  isKnownConversationId,
} from './conversation-branch-guards';
import { useConversationScope } from './useConversationScope';
import { useCreateBranchConversation } from './useCreateBranchConversation';
import { usePendingEditedTurn } from './usePendingEditedTurn';

export type StartConversationBranchInput = {
  messageId: string;
  content: string;
};

export type UseConversationBranchesParams = {
  sendMessage: (content: string) => boolean;
  discardConversation: (
    conversationId: string,
    organizationScope: SidekickActingOrganizationScope
  ) => Promise<void>;
  onEditedTurnRejected?: () => void;
};

export type UseConversationBranchesResult = {
  branchNavigation: ConversationBranchNavigation | null;
  isCreatingBranch: boolean;
  startBranch: (input: StartConversationBranchInput) => Promise<boolean>;
  switchBranch: (conversationId: string) => boolean;
};

/**
 * Creates real backend conversations while retaining branch lineage locally.
 * @param params - Existing conversation submit action
 * @returns Local branch creation, switching, and navigation state
 */
export const useConversationBranches = ({
  sendMessage,
  discardConversation,
  onEditedTurnRejected,
}: UseConversationBranchesParams): UseConversationBranchesResult => {
  const dispatch = useDispatch();
  const store = useStore<StateWithSidekickChat>();
  const messages = useSelector(selectMessages);
  const activeConversationId = useSelector(selectConversationId);
  const scopeVersion = useSelector(selectConversationScopeVersion);
  const isStreaming = useSelector(selectIsStreaming);
  const branchNavigation = useSelector(selectConversationBranchNavigation);
  const openBranch = useCreateBranchConversation();
  const { captureScope, isScopeCurrent } = useConversationScope(
    activeConversationId,
    scopeVersion
  );

  const [isCreatingBranch, setIsCreatingBranch] = useState(false);
  const isCreatingBranchRef = useRef(false);
  const isMountedRef = useRef(true);
  const pendingEditedTurnRef = usePendingEditedTurn({
    activeConversationId,
    sendMessage,
    discardConversation,
    onEditedTurnRejected,
  });

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const startBranch = useCallback(
    async ({
      messageId,
      content,
    }: StartConversationBranchInput): Promise<boolean> => {
      const trimmedContent = content.trim();
      const chatAtRequestStart = store.getState().aiSidekickChat;
      const latestUserMessage = getLatestUserMessage(messages);
      const branchPointIndex = messages.findIndex(
        (message) => message.id === messageId
      );
      if (
        trimmedContent.length === 0 ||
        activeConversationId === null ||
        chatAtRequestStart.conversationMetadata === null ||
        latestUserMessage?.id !== messageId ||
        branchPointIndex === -1 ||
        chatAtRequestStart.isStreaming ||
        chatAtRequestStart.pendingHostUserMessage !== null ||
        isCreatingBranchRef.current
      ) {
        return false;
      }
      const parentMessages = messages;
      const parentScope = captureScope();
      const organizationScope = captureSidekickActingOrganizationScope();
      const knownConversationIds = new Set(
        Object.keys(chatAtRequestStart.branchSession.branchesByConversationId)
      );
      if (chatAtRequestStart.conversationId !== null) {
        knownConversationIds.add(chatAtRequestStart.conversationId);
      }
      isCreatingBranchRef.current = true;
      setIsCreatingBranch(true);
      try {
        let createdConversation: Awaited<ReturnType<typeof openBranch>>;
        try {
          createdConversation = await openBranch(organizationScope, messageId);
        } catch (error) {
          const currentChat = store.getState().aiSidekickChat;
          if (
            !isMountedRef.current ||
            !isScopeCurrent(parentScope) ||
            !isConversationBranchParentCurrent(
              currentChat,
              activeConversationId,
              parentMessages
            )
          ) {
            return false;
          }
          throw error;
        }
        const currentChat = store.getState().aiSidekickChat;
        const canDiscardBackendConversation =
          !knownConversationIds.has(createdConversation.conversationId) &&
          !isKnownConversationId(
            currentChat,
            createdConversation.conversationId
          );
        const discardCreatedConversation = async (): Promise<void> => {
          if (canDiscardBackendConversation) {
            await discardConversation(
              createdConversation.conversationId,
              organizationScope
            );
          }
        };
        if (
          !isMountedRef.current ||
          !isScopeCurrent(parentScope) ||
          currentChat.conversationId !== activeConversationId
        ) {
          await discardCreatedConversation();
          return false;
        }
        if (
          !isConversationBranchParentCurrent(
            currentChat,
            activeConversationId,
            parentMessages
          )
        ) {
          onEditedTurnRejected?.();
          await discardCreatedConversation();
          return false;
        }
        const editedTurnAccepted = new Promise<boolean>((resolve) => {
          pendingEditedTurnRef.current = {
            conversationId: createdConversation.conversationId,
            content: trimmedContent,
            canDiscardBackendConversation,
            organizationScope,
            resolve,
          };
        });
        dispatch(
          createConversationBranch({
            conversationId: createdConversation.conversationId,
            createdAt: createdConversation.createdAt,
            parentConversationId: activeConversationId,
            branchPointMessageId: messageId,
            messages: currentChat.messages.slice(0, branchPointIndex),
          })
        );
        const adoptedChat = store.getState().aiSidekickChat;
        const adoptedBranch =
          adoptedChat.branchSession.branchesByConversationId[
            createdConversation.conversationId
          ];
        if (
          adoptedChat.conversationId !== createdConversation.conversationId ||
          adoptedChat.branchSession.activeConversationId !==
            createdConversation.conversationId ||
          adoptedBranch?.parentConversationId !== activeConversationId ||
          adoptedBranch?.branchPointMessageId !== messageId
        ) {
          const pendingEditedTurn = pendingEditedTurnRef.current;
          pendingEditedTurnRef.current = null;
          pendingEditedTurn?.resolve(false);
          await discardCreatedConversation();
          onEditedTurnRejected?.();
          return false;
        }
        const accepted = await editedTurnAccepted;
        return accepted;
      } finally {
        isCreatingBranchRef.current = false;
        if (isMountedRef.current) {
          setIsCreatingBranch(false);
        }
      }
    },
    [
      activeConversationId,
      captureScope,
      openBranch,
      discardConversation,
      dispatch,
      isScopeCurrent,
      messages,
      onEditedTurnRejected,
      pendingEditedTurnRef,
      store,
    ]
  );

  const switchBranch = useCallback(
    (conversationId: string): boolean => {
      if (isStreaming || isCreatingBranchRef.current) {
        return false;
      }
      dispatch(switchConversationBranch(conversationId));
      return store.getState().aiSidekickChat.conversationId === conversationId;
    },
    [dispatch, isStreaming, store]
  );

  return { branchNavigation, isCreatingBranch, startBranch, switchBranch };
};
